import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { userTenant } from './user';
import {
  CRMStandaloneForm,
  CustomFieldDefinition,
  StandaloneFormUsage,
  Tenant, TenantFormStatus,
} from '@alucio/aws-beacon-amplify/src/models';
import { CUSTOM_FORM_RECORD_ENTITY } from '@alucio/aws-beacon-amplify/src/API';
import { FormSettings } from 'src/classes/CRM/CRMIndexedDBTypes';
import { VeevaVaultFormSetting } from 'src/classes/CRM/CRMVeevaVaultTypes';
import { Singleton as IndexDbCrm } from 'src/classes/CRM/CRMIndexedDB';
import { CRMHandler, FormTranslatorResponse } from 'src/classes/CRM/CRMHandler';
import { useCRMStatus } from 'src/hooks/useCRMStatus';
import type { CRM_STATUS } from 'src/hooks/useCRMStatus';
import { CRMAvailabilityStatus, CRMSyncStatus } from 'src/state/machines/CRM/crmMachineTypes';
import { CustomFieldValuesMap } from 'src/types/orms';
import { isSalesforceFormSetting, isVeevaVaultFormSetting } from 'src/types/typeguards';

export interface StandaloneForm {
  id: string,
  name: string,
  type: CUSTOM_FORM_RECORD_ENTITY,
  apiName?: string,
  crmFormSetting?: FormSettings,
  veevaCRMFormSetting?: VeevaVaultFormSetting,
}

interface StandaloneFormTranslatorResponse {
  customFields: CustomFieldDefinition[];
  defaultValues: CustomFieldValuesMap;
}

export enum FORMS {
  BEACON,
  CRM
}

interface CRMSelectorOptions {
  formSettingId?: string,
  salesforceFormSettings: FormSettings[],
  veevaVaultFormSettings: VeevaVaultFormSetting[],
  formsToFetch: FORMS[],
  standaloneFormId?: string,
  usage?: StandaloneFormUsage,
}

interface CRMFormSettingsCollection {
  veevaVaultFormSettings: VeevaVaultFormSetting[];
  salesforceFormSettings: FormSettings[];
}

const selOptions = (_, options: CRMSelectorOptions): CRMSelectorOptions => options;
const selRawFormConfig = (_, __, rawFormConfig: CRMFormSettingsCollection) => rawFormConfig;
const selSyncingStatus = (_, __, ___, syncStatus: CRM_STATUS) => syncStatus

export const useGetFormConfigFromIndexDB = () => {
  const { canUseMeetingCRMFields } = useCRMStatus();
  const [rawFormConfig, setRawFormConfig] = useState<CRMFormSettingsCollection>({
    veevaVaultFormSettings: [],
    salesforceFormSettings: [],
  });

  useEffect(() => {
    async function fetchFormConfig(): Promise<void> {
      const response = await IndexDbCrm.getFormSettingsRaw();
      const veevaVaultFormSettings = response?.filter(isVeevaVaultFormSetting);
      const salesforceFormSettings = response?.filter(isSalesforceFormSetting);

      setRawFormConfig({
        veevaVaultFormSettings,
        salesforceFormSettings,
      });
    }

    if (canUseMeetingCRMFields) {
      fetchFormConfig();
    }
  }, [canUseMeetingCRMFields]);

  return rawFormConfig;
};

const tenantStandaloneForms = createSelector(
  userTenant,
  selOptions,
  (tenant: Tenant | undefined, options: CRMSelectorOptions): StandaloneForm[] => {
    const standaloneForms: StandaloneForm[] = [];

    // ** CRM FORMS ** //
    if (options.formsToFetch.includes(FORMS.CRM)) {
      [...options.salesforceFormSettings, ...options.veevaVaultFormSettings].forEach((setting) => {
        const standaloneForm: CRMStandaloneForm | undefined = tenant?.config.crmIntegration?.crmStandaloneForms
          ?.find(({ apiName }) => apiName === setting.apiName);
        const hasValidUsage = !options.usage || (options.usage && standaloneForm?.usage?.includes(options.usage));

        if (setting.isStandaloneForm && standaloneForm && hasValidUsage) {
          if (isVeevaVaultFormSetting(setting)) {
            standaloneForms.push({
              id: standaloneForm.id,
              apiName: setting.apiName,
              name: setting.objectInfo.label,
              type: CUSTOM_FORM_RECORD_ENTITY.CRM_OBJECT,
              veevaCRMFormSetting: setting,
            })
          } else if (isSalesforceFormSetting(setting)) {
            standaloneForms.push({
              id: standaloneForm.id,
              apiName: setting.apiName,
              name: setting.objectInfos.label,
              type: CUSTOM_FORM_RECORD_ENTITY.CRM_OBJECT,
              crmFormSetting: setting,
            })
          }
        }
      });
    }

    // ** TENANT FORMS ** //
    if (options.formsToFetch.includes(FORMS.BEACON)) {
      tenant?.config?.forms?.forEach( (form) => {
        if (form.status === TenantFormStatus.ACTIVE) {
          standaloneForms.push({
            id: form.id,
            name: form.label,
            type: CUSTOM_FORM_RECORD_ENTITY.BEACON_TENANT_FORM,
          })
        }
      });
    }

    return standaloneForms;
  },
);

/** Returns a FormTranslatorResponse based on the selected standalone crm form **/
const crmStandaloneFormConfig = createSelector(
  userTenant,
  tenantStandaloneForms,
  selOptions,
  (tenant: Tenant | undefined,
    forms: StandaloneForm[],
    opts: CRMSelectorOptions): StandaloneFormTranslatorResponse => {
    const { formSettingId } = opts;
    const { salesforceFormSettings, veevaVaultFormSettings } = forms.reduce<CRMFormSettingsCollection>((acc, form) => {
      if (isSalesforceFormSetting(form.crmFormSetting) && form.crmFormSetting.isStandaloneForm) {
        acc.salesforceFormSettings.push(form.crmFormSetting);
      } else if (isVeevaVaultFormSetting(form.veevaCRMFormSetting) && form.veevaCRMFormSetting.isStandaloneForm) {
        acc.veevaVaultFormSettings.push(form.veevaCRMFormSetting);
      }

      return acc;
    }, { veevaVaultFormSettings: [], salesforceFormSettings: [] });

    // FIX: Inject VeevaDisclaimer manually so the Translator has access to it
    const disclaimerSetting = opts.salesforceFormSettings.find(fs => fs.id === 'VeevaDisclaimer');
    if (disclaimerSetting) {
      salesforceFormSettings.push(disclaimerSetting);
    }

    if (tenant?.config.crmIntegration) {
      const response = CRMHandler(tenant?.config.crmIntegration)
        .Translator({ salesforceFormSettings, veevaVaultFormSettings, primaryTable: formSettingId })
        .getFormCustomFieldConfig();
      return { customFields: response.crmCustomFields, defaultValues: response.crmDefaultValues };
    }

    return { customFields: [], defaultValues: {} };
  });

/** Returns a FormTranslatorResponse based on the CRM form response **/
const meetingFormConfig = createSelector(
  userTenant,
  selRawFormConfig,
  (tenant: Tenant | undefined, rawFormConfig: CRMFormSettingsCollection): FormTranslatorResponse => {
    if (tenant?.config.crmIntegration) {
      return CRMHandler(tenant?.config.crmIntegration)
        .Translator(rawFormConfig).getFormCustomFieldConfig();
    }

    return { crmCustomFields: [], crmDefaultValues: {}, isLoading: false };
  })

const crmFormConfig = createSelector(
  userTenant,
  selRawFormConfig,
  selSyncingStatus,
  (tenant: Tenant | undefined,
    rawFormConfig: CRMFormSettingsCollection,
    crmSyncingStatus: CRM_STATUS): FormTranslatorResponse => {
    const baseResponse = {
      crmCustomFields: [],
      crmDefaultValues: {},
    };

    if (
      crmSyncingStatus.availabilityStatus === CRMAvailabilityStatus.DISABLED ||
      (!crmSyncingStatus.hasCRMData && !crmSyncingStatus.isCRMSyncing)
    ) {
      return baseResponse;
    }

    if (crmSyncingStatus.syncStatus === CRMSyncStatus.ERROR && !crmSyncingStatus.hasCRMData) {
      return { ...baseResponse, isLoading: false };
    }

    const noFormSettings = (!rawFormConfig.salesforceFormSettings.length &&
      !rawFormConfig.veevaVaultFormSettings.length);
    const isSyncingOrGettingTheForms = (
      crmSyncingStatus.isCRMSyncing ||
      !rawFormConfig || noFormSettings
    )

    if (isSyncingOrGettingTheForms) {
      return { ...baseResponse, isLoading: true };
    }

    if (tenant?.config.crmIntegration) {
      return CRMHandler(tenant?.config.crmIntegration)
        .Translator(rawFormConfig).getFormCustomFieldConfig();
    }

    return baseResponse
  },
)

// ** Returns the CustomFields for the Main CRM Form ** //
export const useCRMFormConfig = (): ReturnType<typeof meetingFormConfig> => {
  const rawFormConfig = useGetFormConfigFromIndexDB();
  const crmSyncingStatus = useCRMStatus();

  return useSelector((state: RootState) => crmFormConfig(state, undefined, rawFormConfig, crmSyncingStatus));
};

// ** Returns an array of available Custom Forms ** //
export const useTenantStandaloneForms = (formsToFetch: FORMS[], usage?: StandaloneFormUsage):
  ReturnType<typeof tenantStandaloneForms> => {
  const rawFormConfig = useGetFormConfigFromIndexDB();
  const config = useMemo(
    () => {
      return {
        salesforceFormSettings: rawFormConfig.salesforceFormSettings || [],
        veevaVaultFormSettings: rawFormConfig.veevaVaultFormSettings || [],
        formsToFetch,
        usage,
      }
    },
    [rawFormConfig, formsToFetch, usage],
  )

  return useSelector((state: RootState) => tenantStandaloneForms(state, config))
}

// ** Given a CRM standaloneFormId, returns its CustomFields ** //
export const useStandaloneFormConfig = (standaloneFormId: string, mainFormSettingId?: string)
  : ReturnType<typeof crmStandaloneFormConfig> => {
  const { veevaVaultFormSettings, salesforceFormSettings } = useGetFormConfigFromIndexDB();
  const options = useMemo<CRMSelectorOptions>(() => ({
    formSettingId: mainFormSettingId,
    salesforceFormSettings,
    veevaVaultFormSettings,
    formsToFetch: [FORMS.CRM],
    standaloneFormId,
  }),
  [standaloneFormId, veevaVaultFormSettings, salesforceFormSettings, mainFormSettingId]);
  const { availabilityStatus } = useCRMStatus();
  return useSelector((state: RootState) => {
    const hasFormSettings = options.salesforceFormSettings.length ||
      options.veevaVaultFormSettings.length;
    if (!hasFormSettings || availabilityStatus !== CRMAvailabilityStatus.ENABLED) {
      return { customFields: [], defaultValues: {} };
    }
    return crmStandaloneFormConfig(state, options);
  });
}
